import {
  ICallPermissionsChanged,
  ICallUserPermissionRequestCreated,
  ICallUserPermissionsChanged,
  ICallUserPermissionsRequestChanged,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import {
  mapUnauthorizedPermissionsChangedEvent,
  mapUnauthorizedPermissionsMemberChangedEvent,
  mapUnauthorizedPermissionsRequestCreatedEvent,
  mapUnauthorizedPermissionsRequestChangedEvent,
} from '@gem/call/Model/callPermissions/unauthorized.callPermissions/callPermissionsModel.unauthorized.mapper';
import { unauthorizedWsCallsApiNetworkService } from '@gem/call/WsApi/unauthorizedWsCallsApi/UnauthorizedWsCallsApi';
import {
  RealtimeCallEvent_CallChanged,
  RealtimeCallEvent_CallRecordingStarted,
  RealtimeCallEvent_CallRecordingStopped,
  RealtimeCallEvent_MemberChanged,
  RealtimeCallEvent_MemberForceFinished,
  RealtimeCallEvent_MemberForceMuted,
  RealtimeCallEvent_MemberPermissionRequestChanged,
  RealtimeCallEvent_MemberPermissionsChanged,
  RealtimeCallEvent_MemberWasAdded,
  RealtimeCallEvent_MemberWasRemoved,
  RealtimeCallEvent_PermissionsChanged,
  RealtimeCallEvent_MemberPermissionRequestCreated,
  RealtimeCallEvent_WaitingRoomChanged,
  RealtimeCallEvent_WaitingRoomMemberChanged,
  RealtimeCallEvent_MemberRoleChanged,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_realtime_events_pb';
import { WsEventsManager } from '@gem/modules/wsClient/wsManagers';

import {
  ICallMember,
  ICallMemberChanged,
  ICallMemberForceFinished,
  ICallMemberForceMuted,
  ICallMemberWasRemoved,
  ICallRecordingStarted,
  ICallRecordingStopped,
  IMemberRoleChanged,
  IStatusCallChanged,
  IWaitingRoomChanged,
  IWaitingRoomListenerParams,
  IWaitingRoomMember,
  TInitCallListenerFunc,
} from '../callModel.types';
import {
  mapUnauthorizedMemberChanged,
  mapUnauthorizedMemberForceFinished,
  mapUnauthorizedMemberForceMuted,
  mapUnauthorizedMemberWasAdded,
  mapUnauthorizedMemberWasRemoved,
  mapUnauthorizedRecordingStarted,
  mapUnauthorizedRecordingStopped,
  mapUnauthorizedStatusCallChanged,
  mapUnauthorizedB2bWaitingRoomMember,
  mapUnauthorizedB2bWaitingRoomChanged,
  mapUnauthorizedMemberRoleChanged,
} from './callModel.unauthorized.mapper';

let wsEventsManager: WsEventsManager | null = null;

function subscribeCallStatusChangedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: IStatusCallChanged) => void
): () => void {
  return eventsManager.on('callChanged', (data: RealtimeCallEvent_CallChanged) => {
    callback(mapUnauthorizedStatusCallChanged(data));
  });
}

function subscribeCallMemberChangedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallMemberChanged) => void
): () => void {
  return eventsManager.on('memberChanged', (data: RealtimeCallEvent_MemberChanged) => {
    callback(mapUnauthorizedMemberChanged(data));
  });
}

function subscribeRecordingStartedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallRecordingStarted) => void
): () => void {
  return eventsManager.on(
    'callRecordingStarted',
    (data: RealtimeCallEvent_CallRecordingStarted) => {
      callback(mapUnauthorizedRecordingStarted(data));
    }
  );
}

function subscribeRecordingStoppedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallRecordingStopped) => void
): () => void {
  return eventsManager.on(
    'callRecordingStopped',
    (data: RealtimeCallEvent_CallRecordingStopped) => {
      callback(mapUnauthorizedRecordingStopped(data));
    }
  );
}

function subscribeMemberForceMutedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallMemberForceMuted) => void
): () => void {
  return eventsManager.on('memberForceMuted', (data: RealtimeCallEvent_MemberForceMuted) => {
    callback(mapUnauthorizedMemberForceMuted(data));
  });
}

function subscribeMemberForceFinishedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallMemberForceFinished) => void
): () => void {
  return eventsManager.on('memberForceFinished', (data: RealtimeCallEvent_MemberForceFinished) => {
    callback(mapUnauthorizedMemberForceFinished(data));
  });
}

function subscribeMemberWasRemovedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallMemberWasRemoved) => void
): () => void {
  return eventsManager.on('memberWasRemoved', (data: RealtimeCallEvent_MemberWasRemoved) => {
    callback(mapUnauthorizedMemberWasRemoved(data));
  });
}

function subscribeMemberWasAddedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallMember) => void
): () => void {
  return eventsManager.on('memberWasAdded', (data: RealtimeCallEvent_MemberWasAdded) => {
    callback(mapUnauthorizedMemberWasAdded(data));
  });
}

function subscribeCallPermissionsChangedEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallPermissionsChanged) => void
): () => void {
  return eventsManager.on('permissionsChanged', (data: RealtimeCallEvent_PermissionsChanged) => {
    callback(mapUnauthorizedPermissionsChangedEvent(data));
  });
}

function subscribeCallMemberPermissionsChangedEvent(
  eventsManager: WsEventsManager,
  callback: (data: ICallUserPermissionsChanged) => void
): () => void {
  return eventsManager.on(
    'memberPermissionsChanged',
    (data: RealtimeCallEvent_MemberPermissionsChanged) => {
      callback(mapUnauthorizedPermissionsMemberChangedEvent(data));
    }
  );
}

function subscribeCallPermissionsRequestCreatedData(
  eventsManager: WsEventsManager,
  callback: (data: ICallUserPermissionRequestCreated) => void
): () => void {
  return eventsManager.on(
    'memberPermissionRequestCreated',
    (data: RealtimeCallEvent_MemberPermissionRequestCreated) => {
      callback(mapUnauthorizedPermissionsRequestCreatedEvent(data));
    }
  );
}

function subscribeCallPermissionsRequestChangedData(
  eventsManager: WsEventsManager,
  callback: (data: ICallUserPermissionsRequestChanged) => void
): () => void {
  return eventsManager.on(
    'memberPermissionRequestChanged',
    (data: RealtimeCallEvent_MemberPermissionRequestChanged) => {
      callback(mapUnauthorizedPermissionsRequestChangedEvent(data));
    }
  );
}

function subscribeWaitingRoomChangedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: IWaitingRoomChanged) => void
): () => void {
  return eventsManager.on('waitingRoomChanged', (data: RealtimeCallEvent_WaitingRoomChanged) => {
    callback(mapUnauthorizedB2bWaitingRoomChanged(data));
  });
}

function subscribeWaitingRoomMemberChangedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: IWaitingRoomMember) => void
): () => void {
  return eventsManager.on(
    'waitingRoomMemberChanged',
    (data: RealtimeCallEvent_WaitingRoomMemberChanged) => {
      callback(mapUnauthorizedB2bWaitingRoomMember(data));
    }
  );
}

function subscribeMemberRoleChangedRealtimeEvent(
  eventsManager: WsEventsManager,
  callback: (data: IMemberRoleChanged) => void
): () => void {
  return eventsManager.on('memberRoleChanged', (data: RealtimeCallEvent_MemberRoleChanged) => {
    callback(mapUnauthorizedMemberRoleChanged(data));
  });
}

let listeners: Array<() => void> = [];

export function dispatchUnauthorizedRoomRealtimeEvents(): void {
  listeners.forEach((unsubscribe) => {
    unsubscribe();
  });
  listeners = [];
  wsEventsManager = null;
}

export const initUnauthorizedWaitingRoomRealtimeEvents = ({
  onWaitingRoomChanged,
  onWaitingRoomMemberChanged,
  onStatusCallChanged,
}: IWaitingRoomListenerParams): (() => void) => {
  if (!unauthorizedWsCallsApiNetworkService) {
    throw new Error('unauthorizedWsCallsApiNetworkService should be initialized');
  }
  wsEventsManager = new WsEventsManager(unauthorizedWsCallsApiNetworkService);

  if (onStatusCallChanged) {
    listeners.push(subscribeCallStatusChangedRealtimeEvent(wsEventsManager, onStatusCallChanged));
  }

  if (onWaitingRoomChanged) {
    listeners.push(subscribeWaitingRoomChangedRealtimeEvent(wsEventsManager, onWaitingRoomChanged));
  }

  if (onWaitingRoomMemberChanged) {
    listeners.push(
      subscribeWaitingRoomMemberChangedRealtimeEvent(wsEventsManager, onWaitingRoomMemberChanged)
    );
  }

  return dispatchUnauthorizedRoomRealtimeEvents;
};

export function dispatchCallRealtimeEvents(): void {
  listeners.forEach((unsubscribe) => {
    unsubscribe();
  });
  listeners = [];
  wsEventsManager = null;
}

export const initUnauthorizedCallRealtimeEvents: TInitCallListenerFunc = ({
  onUserCallData,
  onRecordingStarted,
  onRecordingStopped,
  onMemberForceMuted,
  onMemberForceFinished,
  onMemberWasRemoved,
  onMemberWasAdded,
  onPermissionsData,
  onMemberPermissionsData,
  onCreatePermissionsRequestData,
  onChangePermissionRequestData,
  onMemberRoleChanged,
  onStatusCallChanged,
}) => {
  if (!unauthorizedWsCallsApiNetworkService) {
    throw new Error('wsUnauthorizedCallsApiNetworkService should be initialized');
  }
  wsEventsManager = new WsEventsManager(unauthorizedWsCallsApiNetworkService);

  if (!wsEventsManager) {
    return null;
  }

  if (onStatusCallChanged) {
    listeners.push(subscribeCallStatusChangedRealtimeEvent(wsEventsManager, onStatusCallChanged));
  }
  if (onMemberForceMuted) {
    listeners.push(subscribeMemberForceMutedRealtimeEvent(wsEventsManager, onMemberForceMuted));
  }
  if (onMemberForceFinished) {
    listeners.push(
      subscribeMemberForceFinishedRealtimeEvent(wsEventsManager, onMemberForceFinished)
    );
  }

  if (onMemberWasAdded) {
    listeners.push(subscribeMemberWasAddedRealtimeEvent(wsEventsManager, onMemberWasAdded));
  }

  if (onMemberWasRemoved) {
    listeners.push(subscribeMemberWasRemovedRealtimeEvent(wsEventsManager, onMemberWasRemoved));
  }

  if (onUserCallData) {
    listeners.push(subscribeCallMemberChangedRealtimeEvent(wsEventsManager, onUserCallData));
  }

  if (onRecordingStarted) {
    listeners.push(subscribeRecordingStartedRealtimeEvent(wsEventsManager, onRecordingStarted));
  }

  if (onRecordingStopped) {
    listeners.push(subscribeRecordingStoppedRealtimeEvent(wsEventsManager, onRecordingStopped));
  }

  if (onPermissionsData) {
    listeners.push(subscribeCallPermissionsChangedEvent(wsEventsManager, onPermissionsData));
  }

  if (onMemberPermissionsData) {
    listeners.push(
      subscribeCallMemberPermissionsChangedEvent(wsEventsManager, onMemberPermissionsData)
    );
  }

  if (onCreatePermissionsRequestData) {
    listeners.push(
      subscribeCallPermissionsRequestCreatedData(wsEventsManager, onCreatePermissionsRequestData)
    );
  }

  if (onChangePermissionRequestData) {
    listeners.push(
      subscribeCallPermissionsRequestChangedData(wsEventsManager, onChangePermissionRequestData)
    );
  }

  if (onMemberRoleChanged) {
    listeners.push(subscribeMemberRoleChangedRealtimeEvent(wsEventsManager, onMemberRoleChanged));
  }

  return dispatchCallRealtimeEvents;
};
